import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  IconButton,
  LinearProgress,
  Paper,
  Divider,
} from "@mui/material";
import {
  Assessment as AssessmentIcon,
  Flag as FlagIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Schedule as ScheduleIcon,
  Star as StarIcon,
  ArrowForward as ArrowForwardIcon,
  Refresh as RefreshIcon,
  GetApp as ExportIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
import { usePerformance } from "../../../hooks/features/usePerformance";
import { useAuth } from "../auth/AuthGuard";
import useNotification from "../../../hooks/common/useNotification";
import ErrorBoundary from "../../common/ErrorBoundary";

const PerformanceDashboard = () => {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const { showSuccess, showError } = useNotification();
  
  const {
    stats,
    appraisals,
    objectives,
    loading,
    fetchAppraisalStats,
    fetchObjectiveStats,
    fetchAppraisals,
    fetchObjectives,
    exportAppraisals
  } = usePerformance();

  const [selectedPeriod, setSelectedPeriod] = useState({
    quarter: '',
    year: new Date().getFullYear()
  });

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        await Promise.all([
          fetchAppraisalStats(selectedPeriod),
          fetchObjectiveStats(selectedPeriod),
          fetchAppraisals({ limit: 5, page: 1 }),
          fetchObjectives({ limit: 5, page: 1 })
        ]);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      }
    };

    fetchDashboardData();
  }, [selectedPeriod]);

  // Handle export
  const handleExport = async () => {
    try {
      await exportAppraisals(selectedPeriod);
      showSuccess('Performance data exported successfully');
    } catch (error) {
      showError('Failed to export performance data');
    }
  };

  // Mock data for charts (replace with actual data from stats)
  const statusData = [
    { name: 'Draft', value: stats?.draft || 0, color: '#90A4AE' },
    { name: 'Self Submitted', value: stats?.self_submitted || 0, color: '#2196F3' },
    { name: 'Peer Reviewed', value: stats?.peer_reviewed || 0, color: '#FF9800' },
    { name: 'HR Reviewed', value: stats?.hr_reviewed || 0, color: '#4CAF50' },
  ];

  const scoreData = [
    { name: 'Q1', self: 7.2, peer: 7.8, hr: 8.1 },
    { name: 'Q2', self: 7.5, peer: 8.0, hr: 8.3 },
    { name: 'Q3', self: 7.8, peer: 8.2, hr: 8.5 },
    { name: 'Q4', self: 8.0, peer: 8.4, hr: 8.6 },
  ];

  const objectiveData = [
    { name: 'Q1', completed: 85, pending: 15 },
    { name: 'Q2', completed: 78, pending: 22 },
    { name: 'Q3', completed: 92, pending: 8 },
    { name: 'Q4', completed: 88, pending: 12 },
  ];

  const StatCard = ({ title, value, icon, color, subtitle, onClick }) => (
    <Card 
      sx={{ 
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        '&:hover': onClick ? { transform: 'translateY(-2px)', boxShadow: 3 } : {}
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h4" component="div" fontWeight="bold" color={color}>
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar sx={{ bgcolor: color, width: 56, height: 56 }}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  const QuickActionCard = ({ title, description, icon, color, onClick }) => (
    <Card 
      sx={{ 
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar sx={{ bgcolor: color }}>
            {icon}
          </Avatar>
          <Box flex={1}>
            <Typography variant="h6" fontWeight="medium">
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>
          <ArrowForwardIcon color="action" />
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Performance Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Overview of performance appraisals and objectives
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => window.location.reload()}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<ExportIcon />}
            onClick={handleExport}
          >
            Export Data
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Appraisals"
            value={stats?.total_appraisals || 0}
            icon={<AssessmentIcon />}
            color="primary.main"
            subtitle={`${stats?.completion_rate || 0}% completed`}
            onClick={() => navigate('/performance/appraisals')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Objectives"
            value={stats?.total_objectives || 0}
            icon={<FlagIcon />}
            color="success.main"
            subtitle={`${stats?.approval_rate || 0}% approved`}
            onClick={() => navigate('/performance/objectives')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending Reviews"
            value={(stats?.draft || 0) + (stats?.self_submitted || 0) + (stats?.peer_reviewed || 0)}
            icon={<PendingIcon />}
            color="warning.main"
            onClick={() => navigate('/performance/appraisals?filter=pending')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Avg Score"
            value={stats?.average_scores?.overall || 'N/A'}
            icon={<StarIcon />}
            color="info.main"
            subtitle="Overall performance"
          />
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} mb={3}>
        {/* Appraisal Status Distribution */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Appraisal Status Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Performance Scores Trend */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Performance Scores Trend
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={scoreData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="self" stroke="#2196F3" name="Self Assessment" />
                  <Line type="monotone" dataKey="peer" stroke="#FF9800" name="Peer Review" />
                  <Line type="monotone" dataKey="hr" stroke="#4CAF50" name="HR Review" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Actions and Recent Activity */}
      <Grid container spacing={3} mb={3}>
        {/* Quick Actions */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                <QuickActionCard
                  title="Create Appraisal"
                  description="Start a new performance appraisal"
                  icon={<AssessmentIcon />}
                  color="primary.main"
                  onClick={() => navigate('/performance/appraisals/create')}
                />
                <QuickActionCard
                  title="Create Objective"
                  description="Set new performance objective"
                  icon={<FlagIcon />}
                  color="success.main"
                  onClick={() => navigate('/performance/objectives/create')}
                />
                <QuickActionCard
                  title="Pending Reviews"
                  description="Review pending appraisals"
                  icon={<PendingIcon />}
                  color="warning.main"
                  onClick={() => navigate('/performance/appraisals/pending')}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Appraisals */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Recent Appraisals
                </Typography>
                <Button
                  size="small"
                  onClick={() => navigate('/performance/appraisals')}
                >
                  View All
                </Button>
              </Box>
              <List dense>
                {appraisals?.slice(0, 5).map((appraisal) => (
                  <ListItem key={appraisal.id} divider>
                    <ListItemAvatar>
                      <Avatar>
                        <PersonIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={`${appraisal.user?.first_name} ${appraisal.user?.sur_name}`}
                      secondary={
                        <Box>
                          <Typography variant="caption" display="block">
                            {appraisal.objective?.title}
                          </Typography>
                          <Chip
                            label={appraisal.status?.replace('_', ' ').toUpperCase()}
                            size="small"
                            color={appraisal.status === 'hr_reviewed' ? 'success' : 'default'}
                          />
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/performance/appraisals/${appraisal.id}`)}
                      >
                        <ArrowForwardIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Objectives */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Recent Objectives
                </Typography>
                <Button
                  size="small"
                  onClick={() => navigate('/performance/objectives')}
                >
                  View All
                </Button>
              </Box>
              <List dense>
                {objectives?.slice(0, 5).map((objective) => (
                  <ListItem key={objective.id} divider>
                    <ListItemAvatar>
                      <Avatar>
                        <FlagIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={objective.title}
                      secondary={
                        <Box>
                          <Typography variant="caption" display="block">
                            {objective.user?.first_name} {objective.user?.sur_name}
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip
                              label={objective.implementation_quarter}
                              size="small"
                              variant="outlined"
                            />
                            <Chip
                              icon={objective.is_approved ? <CheckCircleIcon /> : <ScheduleIcon />}
                              label={objective.is_approved ? 'Approved' : 'Pending'}
                              size="small"
                              color={objective.is_approved ? 'success' : 'warning'}
                            />
                          </Box>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/performance/objectives/${objective.id}`)}
                      >
                        <ArrowForwardIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Objective Progress Chart */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Objective Completion by Quarter
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={objectiveData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="completed" fill="#4CAF50" name="Completed" />
                  <Bar dataKey="pending" fill="#FF9800" name="Pending" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

// Wrapper component with error boundary
const PerformanceDashboardWithErrorBoundary = (props) => {
  return (
    <ErrorBoundary
      fallback={
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            <Typography variant="h6" gutterBottom>
              Unable to load dashboard
            </Typography>
            <Typography variant="body2">
              There was an error loading the performance dashboard. Please try refreshing the page or contact support if the problem persists.
            </Typography>
          </Alert>
        </Box>
      }
    >
      <PerformanceDashboard {...props} />
    </ErrorBoundary>
  );
};

export default PerformanceDashboardWithErrorBoundary;
